import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { $createParagraphNode, $createTextNode, $getRoot } from 'lexical';
import React, { memo, useEffect } from 'react'
import omml2mathml from 'omml2mathml';
import { MathMLToLaTeX } from 'mathml-to-latex';
import { $createEquationNode } from '../../nodes/EquationNode';
import JSZip from 'jszip';
import { uploadImage } from '../../../../../../services/file/image';
import { $createImageNode } from '../../nodes/ImageNode';
import { isEqual } from 'lodash';

const textHtmlMimeType = 'text/html';

const W = "http://schemas.openxmlformats.org/wordprocessingml/2006/main";
const R = "http://schemas.openxmlformats.org/officeDocument/2006/relationships";
const M = "http://schemas.openxmlformats.org/officeDocument/2006/math";
const A  = "http://schemas.openxmlformats.org/drawingml/2006/main";
const WP = "http://schemas.openxmlformats.org/drawingml/2006/wordprocessingDrawing";
const MC = "http://schemas.openxmlformats.org/markup-compatibility/2006";


function parseXml(s) { return new DOMParser().parseFromString(s, "application/xml"); }
function firstNS(el, ns, name){ return el.getElementsByTagNameNS(ns, name)[0] || null; }
function attrNS(el, ns, name) {
  return el ? el.getAttributeNS(ns, name) : null;
}

// Build quick maps of styleId -> 'superscript' | 'subscript' | null
function buildStyleVertAlignMaps(stylesDoc) {
  const maps = { char: new Map(), para: new Map(), defaults: null };
  if (!stylesDoc) return maps;

  const styles = stylesDoc.getElementsByTagNameNS(W, "style");
  for (const s of styles) {
    const type = s.getAttributeNS(W, "type");      // 'character' | 'paragraph' | ...
    const id   = s.getAttributeNS(W, "styleId");
    const rPr  = firstNS(s, W, "rPr");
    let va = null;
    if (rPr) {
      const vert = firstNS(rPr, W, "vertAlign");
      const pos  = firstNS(rPr, W, "position");
      if (vert) {
        const v = attrNS(vert, W, "val");
        if (v === "superscript" || v === "subscript") va = v;
      } else if (pos) {
        const n = parseInt(attrNS(pos, W, "val") || "0", 10);
        if (n > 0) va = "superscript";
        else if (n < 0) va = "subscript";
      }
    }
    if (id && va) {
      if (type === "character") maps.char.set(id, va);
      if (type === "paragraph") maps.para.set(id, va);
    }
  }

  // doc defaults: <w:docDefaults><w:rPrDefault><w:rPr>…
  const rPrDef = stylesDoc.getElementsByTagNameNS(W, "rPrDefault")[0];
  if (rPrDef) {
    const rPr = firstNS(rPrDef, W, "rPr");
    const vert = rPr && firstNS(rPr, W, "vertAlign");
    if (vert) {
      const v = attrNS(vert, W, "val");
      if (v === "superscript" || v === "subscript") maps.defaults = v;
    }
  }
  return maps;
}

// Resolve run vertical alignment considering direct, char style, paragraph style, defaults
function getRunVertAlign(runEl, stylesDoc, paraEl) {
  // 1) direct on run
  const rPr = firstNS(runEl, W, "rPr");
  if (rPr) {
    const vert = firstNS(rPr, W, "vertAlign");
    if (vert) {
      const v = attrNS(vert, W, "val");
      if (v === "superscript" || v === "subscript") return v;
    }else{
        return null;
    }
    const pos = firstNS(rPr, W, "position");
    if (pos) {
      const n = parseInt(attrNS(pos, W, "val") || "0", 10);
      if (n > 0) return "superscript";
      if (n < 0) return "subscript";
    }
  }

  if (!stylesDoc) return null; // no styles to resolve

  const maps = buildStyleVertAlignMaps(stylesDoc);

  // 2) character style on run
  const rStyle = rPr && firstNS(rPr, W, "rStyle");
  if (rStyle) {
    const id = attrNS(rStyle, W, "val");
    if (id && maps.char.has(id)) return maps.char.get(id);
  }

  // 3) paragraph-level default rPr or paragraph style
  if (paraEl) {
    const pPr = firstNS(paraEl, W, "pPr");
    if (pPr) {
      const p_rPr = firstNS(pPr, W, "rPr");
      if (p_rPr) {
        const vert = firstNS(p_rPr, W, "vertAlign");
        if (vert) {
          const v = attrNS(vert, W, "val");
          if (v === "superscript" || v === "subscript") return v;
        }
      }
      const pStyle = firstNS(pPr, W, "pStyle");
      if (pStyle) {
        const id = attrNS(pStyle, W, "val");
        if (id && maps.para.has(id)) return maps.para.get(id);
      }
    }
  }

  // 4) document defaults
  return maps.defaults;
}

// function saveBlob(blob, filename = "download.bin") {
//   const url = URL.createObjectURL(blob);
//   const a = document.createElement("a");
//   a.href = url;
//   a.download = filename;
//   document.body.appendChild(a);
//   a.click();
//   a.remove();
//   URL.revokeObjectURL(url);
// }

function readSrcRect(drawingEl) {
  // path: w:drawing/wp:inline|wp:anchor/a:graphic/a:graphicData/pic:pic/pic:blipFill/a:srcRect
  const graphic = firstNS(drawingEl, A, "graphic");
  if (!graphic) return null;
  const graphicData = firstNS(graphic, A, "graphicData");
  if (!graphicData) return null;

  // Try both namespaces for pic (ms prefix varies)
  const picEl = graphicData.getElementsByTagNameNS("http://schemas.openxmlformats.org/drawingml/2006/picture", "pic")[0];
  if (!picEl) return null;

  const blipFill = picEl.getElementsByTagNameNS("http://schemas.openxmlformats.org/drawingml/2006/picture", "blipFill")[0];
  if (!blipFill) return null;

  const blip = firstNS(blipFill, A, "blip");
  const srcRect = firstNS(blipFill, A, "srcRect");
  const rId = blip?.getAttributeNS(R, "embed") || blip?.getAttributeNS(R, "link");

  const crop = srcRect ? {
    l: +srcRect.getAttribute("l") || 0,
    t: +srcRect.getAttribute("t") || 0,
    r: +srcRect.getAttribute("r") || 0,
    b: +srcRect.getAttribute("b") || 0,
  } : { l:0, t:0, r:0, b:0 };

  return { rId, crop };
}

async function blobFromRid(zip, relMap, rId) {
  const rel = relMap.get(rId);
  if (!rel) return null;
  if (rel.external) return { externalUrl: rel.url };
  const f = zip.file(rel.path);
  if (!f) return null;
  const ab = await f.async("arraybuffer");
  const filename = rel.path.split("/").pop() || "image";
  return { blob: new Blob([ab], { type: guessMime(filename) }), filename };
}

async function cropWithSrcRect(originalBlob, crop) {
  const bmp = await createImageBitmap(originalBlob);
  const W = bmp.width, H = bmp.height;

  const left   = Math.round(W * (crop.l / 100000));
  const top    = Math.round(H * (crop.t / 100000));
  const right  = Math.round(W * (crop.r / 100000));
  const bottom = Math.round(H * (crop.b / 100000));

  const cw = Math.max(1, W - left - right);
  const ch = Math.max(1, H - top - bottom);

  const canvas = document.createElement("canvas");
  canvas.width = cw; canvas.height = ch;
  const ctx = canvas.getContext("2d");
  ctx.drawImage(bmp, left, top, cw, ch, 0, 0, cw, ch);

  const out = await new Promise(res => canvas.toBlob(res, originalBlob.type || "image/png", 0.92));
  return out;
}

function normalizePath(p) {
  const out = [];
  for (const seg of p.split("/")) {
    if (!seg || seg === ".") continue;
    if (seg === "..") out.pop(); else out.push(seg);
  }
  return out.join("/");
}
// function partRelsPath(partPath) {
//   // "word/document.xml" -> "word/_rels/document.xml.rels"
//   const i = partPath.lastIndexOf("/");
//   const dir = partPath.slice(0, i + 1);
//   const file = partPath.slice(i + 1);
//   return `${dir}_rels/${file}.rels`;
// }


async function buildRelMap(zip, partPath) {

//   const relsFile = zip.file(partRelsPath(partPath));
const relsFile = zip.file(partPath);
  const map = new Map();
//   console.log(relsFile)
  if (!relsFile) return map;
  const relsDoc = parseXml(await relsFile.async("text"));
  for (const rel of Array.from(relsDoc.getElementsByTagName("Relationship"))) {
    const id   = rel.getAttribute("Id");
    const tgt  = rel.getAttribute("Target");
    const mode = rel.getAttribute("TargetMode") || "Internal";
    if (!id || !tgt) continue;

    if (mode === "External") {
      map.set(id, { external: true, url: tgt });
    } else {
      // Resolve relative to the part’s directory
      const baseDir = partPath.slice(0, partPath.indexOf("/") + 1);
    //   console.log(baseDir)
      map.set(id, { external: false, path: normalizePath(baseDir + tgt) });
    }
  }
  return map;
}
function guessMime(name) {
  const ext = (name.split(".").pop() || "").toLowerCase();
  switch (ext) {
    case "png": return "image/png";
    case "jpg":
    case "jpeg": return "image/jpeg";
    case "gif": return "image/gif";
    case "bmp": return "image/bmp";
    case "tif":
    case "tiff": return "image/tiff";
    case "svg": return "image/svg+xml";
    case "emf": return "image/emf"; // may not render in browsers
    case "wmf": return "image/wmf"; // may not render in browsers
    default: return "application/octet-stream";
  }
}
// async function blobFromRid(zip, relMap, rId) {
//   const rel = relMap.get(rId);
//   if (!rel) return null;
//   if (rel.external) return { externalUrl: rel.url };

//   const file = zip.file(rel.path);
//   if (!file) return null;

//   const ab  = await file.async("arraybuffer");
//   const name = rel.path.split("/").pop() || "image";
//   const type = guessMime(name);
//   return { blob: new Blob([ab], { type }), filename: name, contentType: type, path: rel.path };
// }
  
// function getExtentPx(drawingEl) {
//   // Optional: extract size from wp:inline/wp:anchor
//   const inline = drawingEl.getElementsByTagNameNS(WP, "inline")[0]
//               || drawingEl.getElementsByTagNameNS(WP, "anchor")[0];
//   if (!inline) return null;
//   const ext = inline.getElementsByTagNameNS(WP, "extent")[0];
//   if (!ext) return null;
//   const cx = parseInt(ext.getAttribute("cx") || "0", 10); // EMUs
//   const cy = parseInt(ext.getAttribute("cy") || "0", 10);
//   const toPx = emu => Math.round(emu / 914400 * 96); // 1in=914400 EMU; 96dpi
//   return { width: toPx(cx), height: toPx(cy) };
// }

// function q(node, ns, name) {
//   return Array.from(node.getElementsByTagNameNS(ns, name));
// }

// function textFromRun(runEl) {
//   // Concatenate all w:t within this run
//   return q(runEl, W, "t").map(t => t.textContent || "").join("");
// }

// function base64DecodeUnicode(str) {
//         return decodeURIComponent(
//           Array.prototype.map
//             .call(
//               atob(str),
//               c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2),
//             )
//             .join(''),
//         );
//       }

const uploadImageToServer = async (file) => {
    try {
            const response = await uploadImage(file)
            const { data, status } = response
            if (status === 200) {
              return data
            }
          } catch (e) {
            // setLoading(false)
            console.log(e)
            // return;
          } finally {
            // setLoading(false)
          }
          return null;
}

const uploadImagesToServerInPara = async (paras, zip, relMap) => {

    const map = new Map();
    for (const para of paras) {
      for (const run of Array.from(para.getElementsByTagNameNS(W, "r"))) {
        const drawings = run.getElementsByTagNameNS(W, "drawing");

        for (const drawing of Array.from(drawings)) {
          // Find any a:blip inside (covers inline/anchor + pic structures)
          const info = readSrcRect(drawing);
          if (!info || !info.rId) continue;

          const img = await blobFromRid(zip, relMap, info.rId);
          if (!img) continue;
        //   console.log(img);

          let uploadBlob = img.blob;

          // Apply crop if any
          if (info.crop.l || info.crop.t || info.crop.r || info.crop.b) {
            uploadBlob = await cropWithSrcRect(img.blob, info.crop);
          }

          const fileToSend = new File([uploadBlob], img.filename, {
            type: uploadBlob.type || img.blob.type,
          });
        //   console.log(fileToSend);
          const url = await uploadImageToServer(fileToSend);
        //   console.log({ url });
          map.set(info.rId, url);
          //download file
          // saveBlob(fileToSend, fileToSend.name);
        }
      }
    }
    return map;
}

const getLatexEquationFromOMML = (node) => {

    const doc = new DOMParser().parseFromString(node.outerHTML, "application/xml")
    const math = omml2mathml(doc)
    if (math) {
    //   console.log({ math });
        const latexEquation = MathMLToLaTeX.convert(
        "<math>" + math.innerHTML + "</math>"
        );
    //   console.log({ latexEquation });
        const outLatex = latexEquation
        .replace(/&nbsp;|\u00A0/g, "") // remove entity and real NBSP
        .replace(/\s{2,}/g, " ") // collapse double spaces (optional)
        .trim(); // trim ends (optional)

        return outLatex;
    }
    return null;
}

const createImageNode = (child, imageUrlsWithId) => {
  const info = readSrcRect(child);
  if (!info || !info.rId) return null;
  const url = imageUrlsWithId.get(info.rId);
  if (!url) return null;
//   console.log({ url });
  const imageNode = $createImageNode({
    altText: "Image",
    src: url,
    maxWidth: 400,
  });
  return imageNode;
};

function splitAtLastWhitespace(str) {
  const m = str.trimEnd().match(/^(.*)\s+(\S.*)$/u);
  return m ? [m[1], m[2]] : [str.trimEnd(), ""];
}

function removeRoundBracketsBeforeNumericSubscript(latex) {
  // Pattern for typical Word→LaTeX chemical elements:
  // e.g. N a, C O, F e, etc. (with spaces between letters)
  const elementPair = '(?:[A-Za-z]\\s*[A-Za-z]?)';
  const subNum = '(?:_\\{\\d+\\}|_\\d+)'; // _{2} or _2

  // Case 1: \left(N a\right)_{2} -> N a_{2}
  const reLeftRight = new RegExp(
    String.raw`\\left\\(\s*(${elementPair})\s*\\right\\)\s*(${subNum})`,
    'g'
  );

  // Case 2: (N a)_2 -> N a_2
  const rePlain = new RegExp(
    String.raw`\\(\s*(${elementPair})\s*\\)\s*(${subNum})`,
    'g'
  );

  let out = latex.replace(reLeftRight, '$1$2').replace(rePlain, '$1$2');

  // Clean up extra spaces (optional but recommended)
  out = out.replace(/\s{2,}/g, ' ').trim();

  return out;
}


const wordToStateConverter = memo (function WordToStateConverter({questionNumberIndex, loadedFile, currentParas = [],
  setLoading, loading,
  wantToRemoveExtraSpaces = false
}) {
    // console.log({html})
    const [editor] = useLexicalComposerContext();

    useEffect(() => {

      (async () => {
        console.log("testes", currentParas.length)
        if (!loadedFile || questionNumberIndex < 0) return;
        if (!Array.isArray(currentParas)) return;
        setLoading(true);

        // 1) Do all I/O / parsing OUTSIDE editor.update
        // const allQuestionParas = await docxToQuestions(loadedFile);
        // if (cancelled) return;
        // if (questionNumberIndex > allQuestionParas.length - 1) return;

        // const questionParas =
        //   allQuestionParas[questionNumberIndex].questionParas;

        const questionParas = currentParas;

        const zip = await JSZip.loadAsync(await loadedFile.arrayBuffer());

        const partPath = "word/_rels/document.xml.rels"; // adjust if you're scanning a header/footer part
        const relMap = await buildRelMap(zip, partPath);
        //   console.log(relMap)

        const imageUrlsWithId = await uploadImagesToServerInPara(
          questionParas,
          zip,
          relMap
        );

        // Optional: keep a Set to avoid uploading duplicates
        // const seenPaths = new Set();

        const stylesEntry = zip.file("word/styles.xml");
        const [stylesXml] = await Promise.all([
          stylesEntry ? stylesEntry.async("text") : Promise.resolve(null),
        ]);

        const stylesDoc = stylesXml ? parseXml(stylesXml) : null;

        

        // 2) Now mutate editor state synchronously
        editor.update(() => {
          const root = $getRoot();
          root.clear(); // (optional) if you want to replace all content

          for (const para of questionParas) {
            // Build ONE Lexical paragraph per DOCX paragraph
            const paragraphNode = $createParagraphNode();

            for (const node of Array.from(para.childNodes)) {
              if (node.nodeType !== 1) continue;

              // Inline math? turn into your EquationNode (inline=true)
              if (node.namespaceURI === M && node.localName === "oMath") {
                const outLatex = getLatexEquationFromOMML(node);
                // console.log({outLatex})
                if (outLatex) {
                  // console.log(wantToRemoveExtraSpaces)
                  // console.log(outLatex)
                  // const finalLatex = wantToRemoveExtraSpaces ? removeRoundBracketsBeforeNumericSubscript(outLatex) : outLatex;
                  // console.log({finalLatex})
                  const eqNode = $createEquationNode(outLatex, true);
                  paragraphNode.append(eqNode);
                }
                continue;
              }

              if (node.namespaceURI === M && node.localName === "oMathPara") {
                const outLatex = getLatexEquationFromOMML(node);
                // console.log({outLatex})
                if (outLatex) {
                  // const finalLatex = wantToRemoveExtraSpaces ? removeRoundBracketsBeforeNumericSubscript(outLatex) : outLatex;
                  // console.log({finalLatex})
                  const eqNode = $createEquationNode(outLatex, true);
                  paragraphNode.append(eqNode);
                }
                continue;
              }

              

              //run
              if (node.namespaceURI === W && node.localName === "r") {
                // console.log("inside paragraph")
                for (const child of Array.from(node.childNodes)) {
                    if (child.nodeType !== 1) continue;

                    // Direct text
                    if (child.namespaceURI === W && child.localName === "t") {
                        const str = child.textContent || "";
                        if(!str) continue;
                        // console.log({str})

                        const va = getRunVertAlign(node, stylesDoc, para); // 'superscript'|'subscript'|null
                        if (va !== null) {
                            // console.log({va})
                          const lastNode = paragraphNode?.getLastChild();
                        //   console.log(lastNode)
                          //get text with that node
                          if (lastNode) {
                            const text = lastNode.getTextContent();
                            if (text) {
                            //   console.log({ text });
                              const [before, after] = splitAtLastWhitespace(text);
                            //   console.log({ before, after });
                                if (after) {
                                  if (va === "superscript") {
                                    const lexicalExpresson = `\{${after}}^{${str.trim()}}`;
                                    const eqNode = $createEquationNode(
                                      lexicalExpresson,
                                      true
                                    );
                                    lastNode.remove();
                                    paragraphNode.append(
                                      $createTextNode(before + " ")
                                    );
                                    paragraphNode.append(eqNode);
                                  } else if (va === "subscript") {
                                    const lexicalExpresson = `\{${after}}_{${str.trim()}}`;
                                    const eqNode = $createEquationNode(
                                      lexicalExpresson,
                                      true
                                    );
                                    lastNode.remove();
                                    paragraphNode.append(
                                      $createTextNode(before + " ")
                                    );
                                    paragraphNode.append(eqNode);
                                  }
                                } else {
                                  if (va === "superscript") {
                                    const lexicalExpresson = `\{${before}}^{${str.trim()}}`;
                                    const eqNode = $createEquationNode(
                                      lexicalExpresson,
                                      true
                                    );
                                    lastNode.remove();
                                    // paragraphNode.append(
                                    //   $createTextNode(before + " ")
                                    // );
                                    paragraphNode.append(eqNode);
                                  } else if (va === "subscript") {
                                    const lexicalExpresson = `\{${before}}_{${str.trim()}}`;
                                    const eqNode = $createEquationNode(
                                      lexicalExpresson,
                                      true
                                    );
                                    lastNode.remove();
                                    // paragraphNode.append(
                                    //   $createTextNode(before + " ")
                                    // );
                                    paragraphNode.append(eqNode);
                                  }
                                }
                            }
                          }
                          
                        }else{
                            if (str) paragraphNode.append($createTextNode(str));
                        }

                        
                        continue;
                    }

                    // Direct drawing (DrawingML image, chart, etc.)
                    if (child.namespaceURI === W && child.localName === "drawing") {
                        const imageNode = createImageNode(child, imageUrlsWithId);
                        if (imageNode) paragraphNode.append(imageNode);
                        continue;
                    } 
                    
                    // Compatibility wrapper that may contain a drawing or pict
                    if (child.namespaceURI === MC && child.localName === "AlternateContent") {
                    const choice = child.getElementsByTagName("mc:Choice")[0] || child.firstElementChild;
                    const fallback = child.getElementsByTagName("mc:Fallback")[0];

                    const drawing = child.getElementsByTagNameNS(W, "drawing")[0];
                    if (drawing) { 
                        const imageNode = createImageNode(drawing, imageUrlsWithId);
                        if (imageNode) paragraphNode.append(imageNode);
                        continue;
                    }

                    // const pict = child.getElementsByTagNameNS(W, "pict")[0];
                    // if (pict) { yield { type: "pict", el: pict }; continue; }

                    // If neither exists, just skip
                    continue;
                    }
                }
                // const str = textFromRun(node);
                // if (str) paragraphNode.append($createTextNode(str));
                // console.log({str})
              }
            }

            // Append the finished paragraph to the root
            root.append(paragraphNode);
            
          }
          setLoading(false);
          updateEditor();

          // Block equations? append as separate nodes (inline=false)
          // const eq = $createEquationNode("a+b", /*inline=*/false);
          // root.append(eq);
        });
      })();

    }, [editor, questionNumberIndex, loadedFile, currentParas]);

    const updateEditor = () =>{
        setTimeout(() => {
            editor.focus();
        }, 3000);
    }

    return null;
}, (prevProps, nextProps) => {
    // console.log("/////////////////////////////")
    //  console.log( prevProps.jsonEditorState === nextProps.jsonEditorState)
    const isSameParas = isEqual(prevProps.currentParas , nextProps.currentParas)
    // console.log({isSameParas})
    return prevProps.questionNumberIndex === nextProps.questionNumberIndex && prevProps.loadedFile === nextProps.loadedFile
     && isSameParas;
})

export default wordToStateConverter